import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useStudentAuth, studentSupabase } from '../../contexts/StudentAuthContext';

const FUNCTIONS_BASE = `${(import.meta.env.VITE_SUPABASE_URL || '').replace(/\/$/, '')}/functions/v1`;

export default function StudentClaim() {
  const { student, loading, signIn } = useStudentAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const claimToken = params.get('claim_token')?.trim() || '';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  if (!loading && student) {
    return <Navigate to="/student/dashboard" replace />;
  }

  if (!claimToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6">
        <div className="max-w-md w-full rounded-2xl border border-stone-200 bg-white p-8 text-center">
          <h1 className="text-xl font-semibold text-stone-900 mb-2">Invite required</h1>
          <p className="text-sm text-stone-600 mb-4">
            Student signup is only available from your event registration confirmation link.
          </p>
          <Link to="/student/login" className="text-sm text-amber-800 hover:underline">
            Already have an account? Sign in
          </Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setInfo(null);
    try {
      const res = await fetch(`${FUNCTIONS_BASE}/claim-pm-student-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          claim_token: claimToken,
          email,
          password,
          full_name: fullName || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.detail || 'Claim failed');

      if (data.existing_student && !data.created_auth_user) {
        setInfo(data.message || 'Access granted. Sign in with your existing password.');
        // Attempt sign-in if they re-entered their password
        try {
          await signIn(email, password);
          navigate('/student/dashboard', { replace: true });
          return;
        } catch {
          navigate(`/student/login?email=${encodeURIComponent(email)}`, { replace: true });
          return;
        }
      }

      await signIn(email, password);
      navigate('/student/dashboard', { replace: true });
    } catch (err) {
      setError(err.message || 'Could not create account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6">
      <form
        onSubmit={handleSubmit}
        className="max-w-md w-full rounded-2xl border border-stone-200 bg-white p-8 space-y-4"
      >
        <div>
          <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Prosperity Mentors</p>
          <h1 className="text-2xl font-semibold text-stone-900">Create student account</h1>
          <p className="text-sm text-stone-600 mt-1">Use the email from your registration.</p>
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {info && <p className="text-sm text-amber-800">{info}</p>}
        <input
          className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm"
          placeholder="Full name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
        />
        <input
          required
          type="email"
          className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          required
          type="password"
          minLength={8}
          className="w-full h-11 rounded-lg border border-stone-300 px-3 text-sm"
          placeholder="Password (min 8 characters)"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button
          type="submit"
          disabled={submitting || !studentSupabase}
          className="w-full h-11 rounded-lg bg-amber-800 text-white text-sm font-medium hover:bg-amber-700 disabled:opacity-60"
        >
          {submitting ? 'Creating…' : 'Create account'}
        </button>
        <p className="text-center text-sm text-stone-500">
          <Link to="/student/login" className="text-amber-800 hover:underline">Sign in instead</Link>
        </p>
      </form>
    </div>
  );
}
